/**
 * Realtime WebSocket client — the connection state machine. [milestone M5-T6]
 *
 * One connection per browser tab. Owns the auth handshake (first frame
 * carries the JWT), the heartbeat (`ping` / `pong` every 25s), reconnect
 * backoff (1s → 2s → 4s → 8s → 30s cap), and the `last_seq` checkpoint so a
 * reconnect picks up where the previous socket left off.
 *
 * The single public surface is `connect(token, callbacks)`: it returns a
 * `disconnect()` cleanup. The caller (the React hook) is responsible for
 * calling `disconnect()` on unmount or when the JWT changes.
 *
 * INTENTIONALLY no React imports here — this module is the dumb pipe; the
 * `useRealtime` hook owns the React lifecycle.
 *
 * URL: `ws://<host>/api/v1/ws` (or `wss://` over TLS). The Vite dev proxy
 * forwards `/api` with `ws: true` so this works as-is in dev too. If the
 * proxy ever fails for WebSocket upgrades (close code 1006 on connect) the
 * fallback is to dial the host-exposed hub directly at
 * `ws://127.0.0.1:8100/api/v1/ws` — but in practice the Vite proxy works.
 *
 * READ-ONLY by contract (CTO guardrail #3): the only client → server frames
 * we ever send are `auth`, `ping`, `subscribe`, `unsubscribe`, `typing`,
 * `viewing`. Never `send`/`create`/`update`/`delete` — those go through REST.
 */

import {
  pongFrameSchema,
  realtimeEventSchema,
  replayGapFrameSchema,
  type RealtimeEvent,
} from '@/lib/realtime/types';

/** Callbacks the React hook hands the connection. Kept narrow on purpose. */
export interface ConnectionCallbacks {
  /** Fired for every successfully-parsed realtime event. */
  onEvent: (event: RealtimeEvent) => void;
  /** Fired when the server tells us the replay buffer trimmed past our
   *  `last_seq` — the consumer should drop caches and refetch. */
  onGap: () => void;
  /** Optional: fired each time the socket opens (after auth succeeds). The
   *  React hook can use it to log / toast a reconnect. */
  onOpen?: () => void;
  /** Optional: fired when the socket closes (any reason). Lets the hook
   *  surface a "Disconnected — reconnecting…" state if it wants. */
  onClose?: (event: CloseEvent) => void;
  /** Optional: fired when the server actively kicks this socket with close
   *  code `SESSION_SUPERSEDED_CLOSE_CODE` (4002) — the agent logged in on
   *  another device and the Hub invalidated this session. The connection
   *  has already stopped its reconnect loop by the time this fires (see the
   *  `close` handler below); the hook should treat this as a hard logout and
   *  redirect, same spirit as the axios 401 path but a distinct destination. */
  onSessionSuperseded?: () => void;
}

/** Close code the Hub sends when this session was superseded by a login on
 *  another device (single-device-login enforcement). Reconnecting after this
 *  would just re-auth with a token the server has already invalidated and
 *  loop forever, so the `close` handler treats it as a permanent shutdown
 *  rather than feeding it into the normal backoff/reconnect path. */
export const SESSION_SUPERSEDED_CLOSE_CODE = 4002;

/** Tunables — exposed as constants so a future change to backoff or
 *  heartbeat cadence has one place to land. */
const HEARTBEAT_INTERVAL_MS = 25_000;
const HEARTBEAT_TIMEOUT_MS = 10_000;
const RECONNECT_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 30_000] as const;
const AUTH_FRAME_TIMEOUT_MS = 10_000;

/** Internal connection state. Held as a closure inside `connect()` so two
 *  parallel `connect()` calls (e.g. a quick logout/login) cannot share state. */
interface InternalState {
  ws: WebSocket | null;
  /** Highest seq we have processed (or replayed). Sent on every reconnect's
   *  auth frame so the server replays only what we missed. */
  lastSeq: number | null;
  /** Index into `RECONNECT_BACKOFF_MS` for the next reconnect attempt; reset
   *  to 0 on a successful open. */
  backoffIndex: number;
  /** Set when the caller wants the connection torn down for good — stops
   *  the reconnect loop. */
  shutdown: boolean;
  heartbeatTimer: number | null;
  heartbeatTimeoutTimer: number | null;
  reconnectTimer: number | null;
  /** Per-attempt promise that resolves when the auth handshake completes
   *  (server didn't close the socket within the timeout). Used to gate the
   *  reconnect-backoff increment. */
  authResolved: boolean;
}

/**
 * Build the WebSocket URL.
 *
 * Browser-side: relative `/api/v1/ws` so the Vite dev proxy in dev and the
 * nginx reverse proxy in prod both Just Work. We compute the absolute URL
 * with the same scheme as the page (`wss:` on `https:`, `ws:` on `http:`).
 */
function buildWebSocketUrl(): string {
  if (typeof window === 'undefined') {
    // SSR / test harness without a `window`. Returning a placeholder URL
    // keeps the type checker happy; the connection.ts module is never
    // exercised without a `window` (the React hook gates it).
    return 'ws://localhost/api/v1/ws';
  }
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/api/v1/ws`;
}

/**
 * Open the WebSocket and run the connection lifecycle.
 *
 * Returns a `disconnect()` function. Calling it sets `shutdown=true`,
 * clears the reconnect timer, and closes the socket with code 1000 if open.
 *
 * The function is intentionally synchronous: callers MUST treat the
 * disconnect handle as the authoritative cleanup; awaiting `connect()`
 * elsewhere would block React's effect flow.
 */
export function connect(
  token: string,
  callbacks: ConnectionCallbacks,
): () => void {
  const state: InternalState = {
    ws: null,
    lastSeq: null,
    backoffIndex: 0,
    shutdown: false,
    heartbeatTimer: null,
    heartbeatTimeoutTimer: null,
    reconnectTimer: null,
    authResolved: false,
  };

  function clearTimers(): void {
    if (state.heartbeatTimer !== null) {
      window.clearInterval(state.heartbeatTimer);
      state.heartbeatTimer = null;
    }
    if (state.heartbeatTimeoutTimer !== null) {
      window.clearTimeout(state.heartbeatTimeoutTimer);
      state.heartbeatTimeoutTimer = null;
    }
    if (state.reconnectTimer !== null) {
      window.clearTimeout(state.reconnectTimer);
      state.reconnectTimer = null;
    }
  }

  function scheduleReconnect(): void {
    if (state.shutdown) return;
    // Cap the index at the last element of the backoff array.
    const delay =
      RECONNECT_BACKOFF_MS[
        Math.min(state.backoffIndex, RECONNECT_BACKOFF_MS.length - 1)
      ];
    state.backoffIndex = Math.min(
      state.backoffIndex + 1,
      RECONNECT_BACKOFF_MS.length - 1,
    );
    state.reconnectTimer = window.setTimeout(() => {
      state.reconnectTimer = null;
      openSocket();
    }, delay);
  }

  function sendHeartbeat(): void {
    if (!state.ws || state.ws.readyState !== WebSocket.OPEN) return;
    try {
      state.ws.send(JSON.stringify({ type: 'ping' }));
    } catch {
      // Send failed — the close handler will fire shortly. Nothing to do here.
      return;
    }
    // Arm the pong-timeout. If the server doesn't reply within
    // HEARTBEAT_TIMEOUT_MS we force-close so the reconnect path takes over.
    if (state.heartbeatTimeoutTimer !== null) {
      window.clearTimeout(state.heartbeatTimeoutTimer);
    }
    state.heartbeatTimeoutTimer = window.setTimeout(() => {
      state.heartbeatTimeoutTimer = null;
      // Force-close — the onclose handler will trigger the reconnect.
      if (state.ws && state.ws.readyState === WebSocket.OPEN) {
        try {
          state.ws.close(4000, 'heartbeat-timeout');
        } catch {
          // ignored
        }
      }
    }, HEARTBEAT_TIMEOUT_MS);
  }

  function startHeartbeat(): void {
    if (state.heartbeatTimer !== null) {
      window.clearInterval(state.heartbeatTimer);
    }
    state.heartbeatTimer = window.setInterval(
      sendHeartbeat,
      HEARTBEAT_INTERVAL_MS,
    );
  }

  function handleMessage(raw: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Server should never send non-JSON; log and ignore.
       
      console.warn('realtime: non-JSON frame', raw);
      return;
    }

    // Pong → cancel the pending heartbeat timeout, do not pass through.
    const pong = pongFrameSchema.safeParse(parsed);
    if (pong.success) {
      if (state.heartbeatTimeoutTimer !== null) {
        window.clearTimeout(state.heartbeatTimeoutTimer);
        state.heartbeatTimeoutTimer = null;
      }
      return;
    }

    // Replay gap → invoke the caller's onGap handler and skip the event path.
    const gap = replayGapFrameSchema.safeParse(parsed);
    if (gap.success) {
      // The server has trimmed past our last_seq. Reset our checkpoint so the
      // next reconnect starts fresh (live-only) — the consumer will refetch
      // affected caches via onGap.
      state.lastSeq = null;
      callbacks.onGap();
      return;
    }

    // Real event → validate against the discriminated union, advance lastSeq,
    // dispatch.
    const event = realtimeEventSchema.safeParse(parsed);
    if (!event.success) {
      // Unknown / malformed event — log and skip; don't take down the stream.
       
      console.warn('realtime: bad event envelope', parsed, event.error.issues);
      return;
    }
    // The discriminated union IS the gate: every envelope that parses is a
    // legal client event (its `type` is one of `EVENT_TYPES`), so it is
    // forwarded — no second, hand-kept type allowlist here. One used to sit
    // on this line and silently dropped `message.reaction` (bug 6): live
    // reactions never reached the projector, and neither did their `seq`.
    const value: RealtimeEvent = event.data;
    // Track highest seq we've observed (events can arrive out of order on a
    // pathological day; Math.max is the conservative checkpoint).
    state.lastSeq =
      state.lastSeq === null ? value.seq : Math.max(state.lastSeq, value.seq);
    callbacks.onEvent(value);
  }

  function openSocket(): void {
    if (state.shutdown) return;
    state.authResolved = false;
    const url = buildWebSocketUrl();
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      // `new WebSocket()` can throw synchronously (e.g. malformed URL); fall
      // through to reconnect rather than letting the exception bubble.

      console.warn('realtime: WebSocket constructor failed', err);
      scheduleReconnect();
      return;
    }
    state.ws = ws;

    // Arm an auth-timeout — if the server doesn't accept our auth frame and
    // doesn't close the socket within AUTH_FRAME_TIMEOUT_MS, force-close and
    // reconnect.
    const authTimer = window.setTimeout(() => {
      if (!state.authResolved && ws.readyState === WebSocket.OPEN) {
        try {
          ws.close(4001, 'auth-timeout');
        } catch {
          // ignored
        }
      }
    }, AUTH_FRAME_TIMEOUT_MS);

    ws.addEventListener('open', () => {
      // React 18 StrictMode foot-gun: if `disconnect()` was called while this
      // socket was still CONNECTING, `state.shutdown` is true by the time the
      // open handler runs. We MUST tear down here — sending auth would
      // register a second, unmanaged subscriber at the Hub for this agent and
      // every event published from then on would fan out to both sockets
      // (the live one and this leaked one), doubling presence/unread bumps
      // and keeping a stale heartbeat timer alive in memory. Close cleanly
      // with code 1000 and bail before touching `state` (the React effect
      // has moved on; the freshly-mounted second effect owns the live
      // socket via its own `connect()` closure).
      if (state.shutdown) {
        window.clearTimeout(authTimer);
        try {
          ws.close(1000, 'shutdown-while-connecting');
        } catch {
          // ignored — already closing
        }
        return;
      }
      // Send the auth frame as the very first text frame — backend contract
      // (hub/app/api/ws.py `_authenticate_first_frame`).
      try {
        ws.send(
          JSON.stringify({
            type: 'auth',
            token,
            last_seq: state.lastSeq,
          }),
        );
      } catch (err) {

        console.warn('realtime: failed to send auth frame', err);
        try {
          ws.close();
        } catch {
          // ignored
        }
        return;
      }
      // We've successfully sent auth; reset the backoff and start the
      // heartbeat. Mark authResolved so the auth timeout is a no-op.
      // (If the server closes us with 1008 because the token was bad, the
      // onclose handler still runs the reconnect path — but the backoff
      // reset is fine because the next attempt will fail at the same step.)
      window.clearTimeout(authTimer);
      state.authResolved = true;
      state.backoffIndex = 0;
      startHeartbeat();
      callbacks.onOpen?.();
    });

    ws.addEventListener('message', (ev) => {
      // If the caller has shut down, swallow late frames. The orphan-socket
      // close path below tears the WS down, but a frame already in the
      // browser's queue can still fire before close — projecting it would
      // mutate the cache from a connection the React tree no longer
      // believes in.
      if (state.shutdown) return;
      if (typeof ev.data !== 'string') {
        // Binary frames are not used by the contract; ignore.
        return;
      }
      handleMessage(ev.data);
    });

    ws.addEventListener('close', (ev) => {
      window.clearTimeout(authTimer);
      clearTimers();
      state.ws = null;

      // Session superseded by a login on another device — an active kick,
      // not a transient drop. Reconnecting would immediately re-auth with a
      // session the Hub has already invalidated and get 4002'd again in a
      // loop, so this is a permanent shutdown: set the flag BEFORE the
      // normal reconnect logic below, fire the dedicated callback instead of
      // the generic `onClose` (the hook wants a distinct redirect, not a
      // "Disconnected — reconnecting…" state), and bail out entirely.
      if (ev.code === SESSION_SUPERSEDED_CLOSE_CODE) {
        state.shutdown = true;
        callbacks.onSessionSuperseded?.();
        return;
      }

      // Suppress the onClose callback if the caller asked to shut down —
      // an unsolicited "Disconnected" toast on a planned shutdown (logout,
      // strict-mode unmount) is noise.
      if (!state.shutdown) {
        callbacks.onClose?.(ev);
      }
      // Reconnect unless the caller asked us to shut down OR the server
      // closed us with a clean 1000 (normal shutdown — our own
      // `disconnect()` does this, and the server side does it on offline).
      if (state.shutdown) return;
      // 1000 from the server can happen on a hub restart — we still want to
      // reconnect in that case, so don't treat it as "stop". Only OUR own
      // disconnect() sets state.shutdown.
      scheduleReconnect();
    });

    ws.addEventListener('error', () => {
      // Errors are followed by 'close'; nothing to do here besides log.
      // (Console noise is intentional — the developer wants to see this in
      // dev when the hub is down.)

      console.warn('realtime: WebSocket error event');
    });
  }

  openSocket();

  return function disconnect(): void {
    state.shutdown = true;
    clearTimers();
    // Close the socket regardless of readyState. A CONNECTING socket left to
    // finish opening would (a) trigger its 'open' handler — which we now
    // also gate on `state.shutdown` for belt-and-braces — and (b) waste a
    // Hub WS slot until the orphan times out. Calling close() on a CONNECTING
    // socket aborts the handshake; on CLOSED/CLOSING it is a no-op.
    if (state.ws) {
      try {
        state.ws.close(1000, 'shutdown');
      } catch {
        // ignored — close() throws only when the socket is already CLOSED
        // and the runtime is strict about double-close, which Chrome / Firefox
        // never are.
      }
    }
    state.ws = null;
  };
}
